import {
  AutomationActionTypeByTargetType,
  AutomationMagicFieldTypeName,
  AutomationTriggerType,
  IAutomation,
  IAutomationRuleBounds,
  IAutomationTargetProperty,
  IAutomationTask,
} from "../models/automation";
import { ICustomField, ICustomFieldsState } from "@gtmhub/customFields";
import { IAssigneeIdMap } from "@gtmhub/assignees";
import { SupportedAutomationActionsUI } from "../controllers/action";

export const getCustomFieldsByAutomationType = (cfState: ICustomFieldsState, automation: IAutomation<AutomationTriggerType>): ICustomField[] => {
  if (!automation.trigger || !automation.trigger.objectType) {
    return [];
  }

  return Object.values(cfState.map)
    .reduce((arr, cf) => [...arr, ...cf], [])
    .filter((cf: ICustomField) => cf.targetTypes.includes(automation.trigger.objectType));
};

export const isDeletedCustomField = (customFields: ICustomField[], rule: IAutomationRuleBounds): boolean => {
  return customFields.reduce((isDeleted: boolean, cf: ICustomField) => {
    if (cf.name === rule.fieldName) {
      rule.customFieldDisplayName = cf.label;
      isDeleted = false;
    }

    return isDeleted;
  }, true);
};

export const manuallyMultiplyBy100 = (value: number): number => {
  const valueArr = value.toString().split("");
  const dotIndex = valueArr.findIndex((el) => el === ".");

  if (dotIndex === -1) {
    valueArr.push("00");
  } else {
    valueArr.splice(dotIndex, 3, valueArr[dotIndex + 1], valueArr[dotIndex + 2], ".");
  }

  const result = valueArr.map((el) => (el === undefined ? "0" : el)).join("");

  return Number(result);
};

export const filterDeletedRecipients = (recipients: string[], assigneesIdMap: IAssigneeIdMap): string[] => {
  if (!recipients) {
    return [];
  }

  return recipients.filter((recipient) => assigneesIdMap[recipient]);
};

export const enrichRuleOperators = (properties: IAutomationTargetProperty[]) => {
  return properties.map((propery) => {
    if (propery.dataType === "date") {
      propery.ruleOperators = [...propery.ruleOperators, "inLast", "notInLast"];
    }

    return propery;
  });
};

export const enrichAggregator =
  (...fns) =>
  (x) =>
    fns.reduce((value, fn) => fn(value), x);

export const isPredefinedRuleValue = (rule: IAutomationRuleBounds) => {
  return rule.value.search(/(day$)|(days$)|(week$)|(weeks$)/) > -1;
};

export const isPredefinedDateValue = (input: string): boolean => {
  return input && input.search(/(day$)|(days$)|(week$)|(weeks$)/) > -1;
};

export const supportedAutomationActionsUI = (actionCopies: Record<AutomationActionTypeByTargetType[AutomationTriggerType], string>): SupportedAutomationActionsUI => ({
  goal: {
    gtmhub: {
      actions: [
        {
          displayName: actionCopies.tag,
          actionType: "tag",
        },
        {
          displayName: actionCopies.create_task,
          actionType: "create_task",
        },
        {
          displayName: actionCopies.delete_item,
          actionType: "delete_item",
        },
      ],
    },
    external: {
      actions: [
        {
          displayName: actionCopies.email,
          actionType: "email",
        },
        {
          displayName: actionCopies.send_slack_message,
          actionType: "send_slack_message",
        },
      ],
    },
  },
  metric: {
    gtmhub: {
      actions: [
        {
          displayName: actionCopies.create_task,
          actionType: "create_task",
        },
        {
          displayName: actionCopies.delete_item,
          actionType: "delete_item",
        },
      ],
    },
    external: {
      actions: [
        {
          displayName: actionCopies.email,
          actionType: "email",
        },
        {
          displayName: actionCopies.send_slack_message,
          actionType: "send_slack_message",
        },
      ],
    },
  },
  user: {
    gtmhub: {
      actions: [
        {
          displayName: actionCopies.delete_item,
          actionType: "delete_item",
        },
        {
          displayName: actionCopies.assign_role,
          actionType: "assign_role",
        },
        {
          displayName: actionCopies.unassign_role,
          actionType: "unassign_role",
        },
      ],
    },
    external: {
      actions: [
        {
          displayName: actionCopies.email,
          actionType: "email",
        },
      ],
    },
  },
});

export const filterSupportedAutomationActions = <T extends AutomationTriggerType>(
  actions: {
    displayName: string;
    actionType: AutomationActionTypeByTargetType[T];
  }[],
  supportedActions: AutomationActionTypeByTargetType[T][]
) => {
  return actions.filter((action) => supportedActions.includes(action.actionType));
};

export const magicFieldCopies: { [TypeName in AutomationMagicFieldTypeName]: string } = {
  Objective: "Objective",
  Metric: "Key Result",
  Session: "Session",
  Assignee: "Owner",
  Creator: "Creator",
  User: "User",
};

export const checkIfActiveStatusConditionExists = (automationTask: IAutomationTask) => {
  let currentAutomationTask = automationTask;
  let activeStatusConditionMissing = true;

  while (currentAutomationTask && activeStatusConditionMissing) {
    if (currentAutomationTask.rule && currentAutomationTask.rule.ruleBounds) {
      activeStatusConditionMissing = !currentAutomationTask.rule.ruleBounds.some((rule) => {
        return rule.fieldName === "IsActive" && ((rule.operator === "equals" && rule.value == "false") || (rule.operator === "notEqual" && rule.value == "true"));
      });

      if (!activeStatusConditionMissing) return !activeStatusConditionMissing;
    }

    currentAutomationTask = currentAutomationTask.nextAutomationTask;
  }

  return !activeStatusConditionMissing;
};
